import React, { useState } from "react";
import { motion } from "framer-motion";
import { Building2, ShieldCheck, Users, GraduationCap, Check, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { api } from "../lib/api";

const L = {
  de: {
    kicker: "Für Ställe & Vereine",
    title: "Sicherheitskultur für deinen Stall",
    sub: "Circle of Ikarus bringt die systematische Sicherheitskultur der Luftfahrt in Reitbetriebe – als Partner für Ausbildung, Standards und Prävention.",
    benefitsTitle: "Was Partner-Ställe bekommen",
    benefits: [
      { icon: "ShieldCheck", t: "Pre-Ride-Kultur", d: "Checklisten & Standards für Halle, Platz und Ausrüstung – sichtbar und alltagstauglich." },
      { icon: "GraduationCap", t: "Schulungen", d: "Human-Factors- & Para-aware-Module für Reiter:innen, Personal und Helfer." },
      { icon: "Users", t: "Community & Reichweite", d: "Gemeinsame Aktionstage und Sichtbarkeit über unsere Kanäle." },
      { icon: "ClipboardList", t: "Vorfall-Kultur", d: "Near-Miss-Logbuch & anonyme Auswertung als Basis echter Prävention." },
    ],
    formTitle: "Als Safety-Partner anfragen",
    stable: "Stall / Verein",
    name: "Ansprechpartner:in",
    email: "E-Mail",
    message: "Deine Nachricht",
    send: "Anfrage senden",
    ok: "Danke! Deine Anfrage ist angekommen – wir melden uns.",
  },
  en: {
    kicker: "For stables & clubs",
    title: "A safety culture for your stable",
    sub: "Circle of Ikarus brings aviation's systematic safety culture to riding establishments – as a partner for education, standards and prevention.",
    benefitsTitle: "What partner stables get",
    benefits: [
      { icon: "ShieldCheck", t: "Pre-ride culture", d: "Checklists & standards for arena, field and equipment – visible and practical." },
      { icon: "GraduationCap", t: "Training", d: "Human-factors & para-aware modules for riders, staff and helpers." },
      { icon: "Users", t: "Community & reach", d: "Joint action days and visibility across our channels." },
      { icon: "ClipboardList", t: "Incident culture", d: "Near-miss logbook & anonymous analysis as a basis for real prevention." },
    ],
    formTitle: "Apply as a safety partner",
    stable: "Stable / club",
    name: "Contact person",
    email: "Email",
    message: "Your message",
    send: "Send inquiry",
    ok: "Thank you! Your inquiry has arrived – we'll be in touch.",
  },
};

const icons = { ShieldCheck, GraduationCap, Users, ClipboardList };

export default function Stalls() {
  const { lang } = useLang();
  const t = L[lang];
  const [form, setForm] = useState({ stable: "", name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        topic: "b2b",
        message: `[Stall/Verein: ${form.stable}] ${form.message}`,
      });
      setSent(true);
      toast.success(t.ok);
      setForm({ stable: "", name: "", email: "", message: "" });
    } catch { toast.error("Fehler / Error"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:py-16">
      <div className="flex items-center gap-2 instrument-label text-sonne"><Building2 size={16} /> {t.kicker}</div>
      <h1 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight">{t.title}</h1>
      <p className="mt-4 text-wachs-dim max-w-2xl leading-relaxed">{t.sub}</p>

      <h2 className="mt-10 font-serif text-2xl text-sonne">{t.benefitsTitle}</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {t.benefits.map((b) => {
          const Icon = icons[b.icon] || ShieldCheck;
          return (
            <motion.div key={b.t} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              data-testid={`b2b-benefit-${b.icon}`} className="grid-panel rounded-lg p-5">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-md bg-sonne/12 border border-sonne/30 flex items-center justify-center text-sonne"><Icon size={18} /></span>
                <h3 className="font-serif text-xl">{b.t}</h3>
              </div>
              <p className="mt-3 text-sm text-wachs-dim leading-relaxed">{b.d}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-12 grid-panel rounded-xl p-6 md:p-8 max-w-2xl">
        <h2 className="font-serif text-2xl">{t.formTitle}</h2>
        {sent ? (
          <div className="mt-4 flex items-center gap-2 text-ready" data-testid="b2b-success"><Check size={18} /> {t.ok}</div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="b2b-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className="instrument-label text-wachs-dim">{t.stable}</span>
                <input required value={form.stable} onChange={(e) => setForm({ ...form, stable: e.target.value })} data-testid="b2b-stable" className="coi-input mt-1.5" /></label>
              <label className="block"><span className="instrument-label text-wachs-dim">{t.name}</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="b2b-name" className="coi-input mt-1.5" /></label>
            </div>
            <label className="block"><span className="instrument-label text-wachs-dim">{t.email}</span>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="b2b-email" className="coi-input mt-1.5" /></label>
            <label className="block"><span className="instrument-label text-wachs-dim">{t.message}</span>
              <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="b2b-message" className="coi-input mt-1.5 resize-none" /></label>
            <button type="submit" data-testid="b2b-submit" className="bg-bronze text-nacht font-medium px-6 py-3 rounded-md hover:bg-bronze-soft transition-colors">{t.send}</button>
          </form>
        )}
      </div>
    </div>
  );
}
